import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DihybridCrossCalculator = () => {
  const [trait1Parent1, setTrait1Parent1] = useState('');
  const [trait1Parent2, setTrait1Parent2] = useState('');
  const [trait2Parent1, setTrait2Parent1] = useState('');
  const [trait2Parent2, setTrait2Parent2] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateCross = () => {
    if (!trait1Parent1 || !trait1Parent2 || !trait2Parent1 || !trait2Parent2) {
      setError('Please enter all genotype information');
      return;
    }

    const alleles = [trait1Parent1, trait1Parent2, trait2Parent1, trait2Parent2];
    const invalidAlleles = alleles.filter(allele => 
      allele.length !== 2 || 
      allele[0] !== allele[0].toUpperCase() || 
      allele[1] !== allele[1].toLowerCase()
    );

    if (invalidAlleles.length > 0) {
      setError('Please enter alleles in the format Aa (uppercase for dominant, lowercase for recessive)');
      return;
    }

    const parent1Gametes = [
      trait1Parent1[0] + trait2Parent1[0],
      trait1Parent1[0] + trait2Parent1[1],
      trait1Parent1[1] + trait2Parent1[0],
      trait1Parent1[1] + trait2Parent1[1]
    ];

    const parent2Gametes = [
      trait1Parent2[0] + trait2Parent2[0],
      trait1Parent2[0] + trait2Parent2[1],
      trait1Parent2[1] + trait2Parent2[0],
      trait1Parent2[1] + trait2Parent2[1]
    ];

    const punnettSquare = [];
    const genotypeCounts = {};

    for (let i = 0; i < 4; i++) {
      const row = [];
      for (let j = 0; j < 4; j++) {
        const genotype = sortGenotype(parent1Gametes[i] + parent2Gametes[j]);
        row.push(genotype);
        genotypeCounts[genotype] = (genotypeCounts[genotype] || 0) + 1;
      }
      punnettSquare.push(row);
    }

    const phenotypeCounts = {};
    Object.keys(genotypeCounts).forEach(genotype => {
      const phenotype = getPhenotype(genotype);
      phenotypeCounts[phenotype] = (phenotypeCounts[phenotype] || 0) + genotypeCounts[genotype];
    });

    const newResults = {
      punnettSquare,
      genotypeCounts,
      phenotypeCounts,
      parent1Gametes,
      parent2Gametes
    };

    setResults(newResults);
    setError('');

    const sortedPhenotypes = Object.entries(phenotypeCounts).sort((a, b) => b[1] - a[1]);
    setChartData({
      bar: {
        labels: sortedPhenotypes.map(([phenotype]) => phenotype),
        datasets: [{
          label: 'Phenotype Distribution',
          data: sortedPhenotypes.map(([, count]) => count),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }]
      },
      pie: {
        labels: sortedPhenotypes.map(([phenotype]) => phenotype),
        datasets: [{
          label: 'Phenotype Distribution',
          data: sortedPhenotypes.map(([, count]) => count),
          backgroundColor: [
            'rgba(75, 192, 192, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 159, 64, 0.7)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const sortGenotype = (genotype) => {
    const alleles = genotype.split('');
    alleles.sort((a, b) => {
      if (a === a.toUpperCase() && b === b.toLowerCase()) return -1;
      if (a === a.toLowerCase() && b === b.toUpperCase()) return 1;
      return a.localeCompare(b);
    });
    return alleles.join('');
  };

  const getPhenotype = (genotype) => {
    if (!genotype) return '';
    
    const sortedGenotype = sortGenotype(genotype);
    
    const traits = sortedGenotype.match(/([A-Z][a-z])/g);
    if (!traits) return '';

    const phenotype = traits.map(trait => {
      return trait[0] === trait[0].toUpperCase() ? 'Dominant' : 'Recessive';
    });
    
    return `Trait 1: ${phenotype[0]}, Trait 2: ${phenotype[1]}`;
  };

  const exampleData = {
    parent1: {
      trait1: 'Aa',
      trait2: 'Bb'
    },
    parent2: {
      trait1: 'Aa',
      trait2: 'Bb'
    }
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-gray-50">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="text-4xl">🧬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dihybrid Cross Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Genetic Inheritance Predictor</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-8 mb-8 max-w-4xl mx-auto"
      >
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Parent 1 Genotypes</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trait 1 (e.g., Aa):</label>
                  <input
                    type="text"
                    value={trait1Parent1}
                    onChange={(e) => setTrait1Parent1(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter genotype (e.g., Aa)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trait 2 (e.g., Bb):</label>
                  <input
                    type="text"
                    value={trait2Parent1}
                    onChange={(e) => setTrait2Parent1(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter genotype (e.g., Bb)"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Parent 2 Genotypes</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trait 1 (e.g., Aa):</label>
                  <input
                    type="text"
                    value={trait1Parent2}
                    onChange={(e) => setTrait1Parent2(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter genotype (e.g., Aa)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trait 2 (e.g., Bb):</label>
                  <input
                    type="text"
                    value={trait2Parent2}
                    onChange={(e) => setTrait2Parent2(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter genotype (e.g., Bb)"
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={calculateCross}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200"
          >
            Calculate Cross
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Genetic Cross Analysis</h2>
              
              <div className="bg-white p-6 rounded-xl shadow-md mb-12">
                <h3 className="text-xl font-semibold mb-4">Punnett Square</h3>
                <div className="grid grid-cols-5 gap-4 bg-gray-100 p-4 rounded-md">
                  <div className="bg-gray-200 p-4 font-bold text-center">Parent 2 Gametes</div>
                  {results.parent2Gametes.map((gamete, i) => (
                    <div key={i} className="bg-gray-200 p-4 text-center">{gamete}</div>
                  ))}
                  {results.punnettSquare.map((row, i) => (
                    <React.Fragment key={i}>
                      <div className="bg-gray-200 p-4 text-center">{results.parent1Gametes[i]}</div>
                      {row.map((genotype, j) => (
                        <div key={j} className="bg-white p-4 text-center">{genotype}</div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Genotype Distribution</h3>
                  <ul className="space-y-3">
                    {Object.entries(results.genotypeCounts).map(([genotype, count]) => (
                      <li key={genotype} className="flex justify-between">
                        <span>{genotype}</span>
                        <span>{count} offspring</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Phenotype Distribution</h3>
                  <ul className="space-y-3">
                    {Object.entries(results.phenotypeCounts).map(([phenotype, count]) => (
                      <li key={phenotype} className="flex justify-between">
                        <span>{phenotype}</span>
                        <span>{count} offspring</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {chartData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-xl font-semibold mb-4">Bar Chart</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData.bar}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Number of Offspring'
                              }
                            },
                            x: {
                              title: {
                                display: true,
                                text: 'Phenotype'
                              }
                            }
                          },
                          plugins: {
                            title: {
                              display: true,
                              text: 'Phenotype Distribution'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-xl font-semibold mb-4">Pie Chart</h3>
                    <div className="h-96">
                      <Pie
                        data={chartData.pie}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            title: {
                              display: true,
                              text: 'Phenotype Distribution'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a classic dihybrid cross:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Parent Genotypes:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Parent 1: {exampleData.parent1.trait1} (Trait 1), {exampleData.parent1.trait2} (Trait 2)</li>
              <li>Parent 2: {exampleData.parent2.trait1} (Trait 1), {exampleData.parent2.trait2} (Trait 2)</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Expected Results:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Genotype Ratio: 9:3:3:1</li>
              <li>Phenotype Ratio: 9:3:3:1</li>
              <li>25% of offspring will be homozygous dominant for both traits</li>
              <li>50% of offspring will be heterozygous for at least one trait</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Dihybrid Cross Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Dihybrid Cross Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Parent Genotypes</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the genotype for each trait for both parents</li>
                <li>Use uppercase letters for dominant alleles (e.g., A)</li>
                <li>Use lowercase letters for recessive alleles (e.g., a)</li>
                <li>Ensure each genotype is in the format Xx (uppercase followed by lowercase)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate the Cross</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Calculate Cross" button</li>
                <li>View the Punnett Square showing all possible offspring combinations</li>
                <li>Check the genotype and phenotype distributions</li>
                <li>Review the phenotype distribution chart</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the Punnett Square for all possible offspring genotypes</li>
                <li>Check the genotype distribution for each combination</li>
                <li>Verify the phenotype distribution and ratios</li>
                <li>Compare results with expected Mendelian ratios</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Ensure correct allele notation (uppercase for dominant, lowercase for recessive)</li>
                <li>Verify that both parents have the same trait alleles (e.g., both use A/a for Trait 1)</li>
                <li>Check for independent assortment of traits</li>
                <li>Consider linkage effects if traits are not independent</li>
                <li>Use the Punnett Square to identify all possible offspring combinations</li>
                <li>Compare results with expected Mendelian ratios (9:3:3:1 for independent traits)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Dihybrid Crosses <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            A dihybrid cross is a genetic cross between two individuals that are both heterozygous for two traits. 
            The Punnett Square helps visualize all possible combinations of alleles from both parents.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Genotype:</span> The genetic makeup of an organism (e.g., AaBb)
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Phenotype:</span> The observable characteristics resulting from the genotype
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Independent Assortment:</span> Traits are inherited independently of each other
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Mendelian Ratios:</span> Expected ratios of offspring phenotypes (9:3:3:1)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DihybridCrossCalculator;